import React, {memo} from 'react';
import {WeatherForecastDay, WeatherForecastLocation} from '@/app/interfaces/weather';
import {useCurrentHour} from '@/common/helpers/hooks/useCurrentHour';
import {AdBanner} from '@/app/components/common/advertising';
import {SunTime} from '@/app/components/common/SunTime';
import {WeatherAlerts} from '@/app/components/common/WeatherAlerts';
import {WeatherHistoric} from '../../WeatherHistoric';
import {WeatherShareButton} from '../../WeatherShareButton';
import {WeatherTimezone} from '../../WeatherTimezone';
import {WeatherVerbal} from '../../WeatherVerbal';
import {WeatherDailyCurrentHour} from '../WeatherDailyCurrentHour';
import {WeatherDailyDate} from '../WeatherDailyDate';
import {WeatherDailyHoursTable} from '../WeatherDailyHoursTable';
import {WeatherDailyOverall} from '../WeatherDailyOverall';
import styles from './WeatherDailyDetails.css';

const allowedHours = [0, 3, 6, 9, 12, 15, 18, 21];

const extractFirst = (input: unknown | unknown[]) => (Array.isArray(input) ? input[0] : input);

interface WeatherDailyDetailsProps {
	day: WeatherForecastDay;
	dayIsPast: boolean;
	location: WeatherForecastLocation;
	showBanner: boolean;
}

export const WeatherDailyDetails = memo<WeatherDailyDetailsProps>(props => {
	const {now, date, hours: allHours, historic, sunTime, verbal} = props.day;
	const {dayIsPast, showBanner, location} = props;

	// Cut extra hours to leave maximum two per day period
	let hours = allHours;
	if (hours.length > 8) {
		hours = hours.filter(item => allowedHours.includes(item.hour));
	}

	// Detect the nearest hour to the now one for highlight it as the current
	const currentHour = useCurrentHour(hours, location.timezoneOffset, !!now);

	return (
		<div className={styles.root}>
			<div className={styles.leading}>
				{now ? (
					<>
						{currentHour !== undefined && (
							<WeatherDailyCurrentHour location={location} timezoneOffset={location.timezoneOffset} />
						)}

						<WeatherDailyOverall
							condition={now.condition}
							temperature={now.temperature}
							waterTemperature={now.waterTemperature}
							sunTime={sunTime}
							timezoneOffset={location.timezoneOffset}
						/>
					</>
				) : (
					<WeatherDailyDate date={date} />
				)}

				<div className={styles.extraConditions}>
					<SunTime className={styles.sunTime} sunTime={sunTime} multiline />
					<WeatherTimezone
						targetTitle={location.title}
						targetTitleLc={location.titleLc}
						targetTimezoneOffset={location.timezoneOffset}
					/>
					{historic && (
						<WeatherHistoric
							className={styles.historic}
							date={date}
							historic={historic}
							locationTimezone={location.timezoneName}
						/>
					)}
					<WeatherShareButton location={location} date={date} />
				</div>
			</div>
			<div className={styles.main}>
				<WeatherDailyHoursTable
					hours={hours}
					currentHour={currentHour}
					dayIsPast={dayIsPast}
					sunTime={sunTime}
				/>

				<WeatherAlerts
					className={styles.alerts}
					hours={allHours}
					date={date}
					fromCurrentHour={!!now}
					timezoneOffset={location.timezoneOffset}
				/>

				<WeatherVerbal verbal={verbal} />
			</div>

			{showBanner && (
				<AdBanner
					className={styles.adBar}
					interfaceName='desktop'
					zoneName='weatherSide'
					zoneDataReducer={extractFirst}
					placeholder
				/>
			)}
		</div>
	);
});

WeatherDailyDetails.displayName = 'WeatherDailyDetails';
